import React from 'react';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Typography, 
  Avatar, 
  Divider,
  Chip,
  LinearProgress
} from '@mui/material';
import { 
  faHome, 
  faGraduationCap, 
  faBook, 
  faTrophy,
  faIdCard,
  faUser,
  faStar,
  faChartLine,
  faCog,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';

const MembroMenu: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const menuItems = [
    { 
      label: 'Início', 
      path: '/membro', 
      icon: faHome,
      description: 'Dashboard principal'
    },
    { 
      label: 'Lições', 
      path: '/licao', 
      icon: faGraduationCap,
      description: 'Aprenda e estude',
      badge: '12/20'
    },
    { 
      label: 'Declaração', 
      path: '/declaracao', 
      icon: faBook,
      description: 'Suas declarações'
    },
    { 
      label: 'Rank', 
      path: '/rank', 
      icon: faTrophy,
      description: 'Ranking da comunidade'
    },
    { 
      label: 'Carteirinha', 
      path: '/carteirinha', 
      icon: faIdCard,
      description: 'Sua carteirinha digital'
    }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/inicio/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  return (
    <Box sx={{
      width: 280,
      height: '100vh',
      backgroundColor: '#fff',
      borderRight: '1px solid #e0e0e0',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
      position: 'sticky',
      top: 0
    }}>
      {/* Header */}
      <Box sx={{
        p: 3,
        background: 'linear-gradient(135deg, #225477 0%, #4688ae 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <Avatar 
          src={user?.perfil || '/assets/images/perfil.png'} 
          alt="Perfil"
          sx={{ 
            width: 80, 
            height: 80, 
            mx: 'auto', 
            mb: 2,
            border: '3px solid rgba(255,255,255,0.3)'
          }}
        />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {user?.nome || 'Usuário'}
        </Typography>
        <Chip 
          label="Membro Ativo" 
          size="small" 
          sx={{ 
            backgroundColor: 'rgba(255,255,255,0.2)', 
            color: 'white',
            fontSize: '0.75rem'
          }} 
        />
        
        {/* Progress Bar */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Progresso Geral
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              60%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={60} 
            sx={{ 
              height: 6, 
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#FFD700'
              }
            }} 
          />
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ pt: 2 }}>
          {menuItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: '#225477',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1a4560'
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white'
                    }
                  },
                  '&:hover': {
                    backgroundColor: '#f5f7fa'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <FontAwesomeIcon 
                    icon={item.icon} 
                    style={{ 
                      fontSize: '1.2rem',
                      color: location.pathname === item.path ? 'white' : '#225477'
                    }} 
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  secondary={item.description}
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: location.pathname === item.path ? 600 : 500
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.75rem',
                    color: location.pathname === item.path ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                  }}
                />
                {item.badge && (
                  <Chip 
                    label={item.badge} 
                    size="small" 
                    sx={{ 
                      backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.2)' : '#e3f2fd',
                      color: location.pathname === item.path ? 'white' : '#1976d2',
                      fontSize: '0.7rem',
                      height: 20
                    }} 
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#f5f7fa'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FontAwesomeIcon icon={faCog} style={{ fontSize: '1.1rem', color: '#666' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Configurações"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: '#666'
                }}
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#ffebee'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <FontAwesomeIcon icon={faSignOutAlt} style={{ fontSize: '1.1rem', color: '#d32f2f' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Sair"
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: '#d32f2f'
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );
};

export default MembroMenu; 